import { c as _c } from "react/compiler-runtime";
import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from "react";
type ViewportMetrics = Readonly<{
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}>;
type MutableValueRef<T> = {
  current: T;
};
const EMPTY_VIEWPORT_METRICS: ViewportMetrics = {
  scrollLeft: 0,
  clientWidth: 0,
  scrollWidth: 0
};
function snapViewportToLatest(viewport: HTMLDivElement | null) {
  if (!viewport) {
    return;
  }
  const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  // Show the latest column fully, even when the viewport width is not aligned to a column stride.
  viewport.scrollLeft = maxScrollLeft;
}
function readViewportMetrics(viewport: HTMLDivElement | null, cachedMetricsRef: MutableValueRef<ViewportMetrics>): ViewportMetrics {
  if (!viewport) {
    return EMPTY_VIEWPORT_METRICS;
  }
  const nextMetrics = {
    scrollLeft: viewport.scrollLeft,
    clientWidth: viewport.clientWidth,
    scrollWidth: viewport.scrollWidth
  } as const;
  const previousMetrics = cachedMetricsRef.current;
  if (previousMetrics.scrollLeft === nextMetrics.scrollLeft && previousMetrics.clientWidth === nextMetrics.clientWidth && previousMetrics.scrollWidth === nextMetrics.scrollWidth) {
    return previousMetrics;
  }
  cachedMetricsRef.current = nextMetrics;
  return nextMetrics;
}
function subscribeToViewportMetrics(viewport: HTMLDivElement | null, onStoreChange: () => void) {
  if (!viewport) {
    return () => {};
  }
  const content = viewport.firstElementChild;
  let frameId = 0;
  const notify = () => {
    cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(onStoreChange);
  };
  notify();
  viewport.addEventListener("scroll", notify, {
    passive: true
  });
  if (typeof ResizeObserver === "undefined") {
    window.addEventListener("resize", notify);
    return () => {
      cancelAnimationFrame(frameId);
      viewport.removeEventListener("scroll", notify);
      window.removeEventListener("resize", notify);
    };
  }
  const resizeObserver = new ResizeObserver(notify);
  resizeObserver.observe(viewport);
  if (content) {
    resizeObserver.observe(content);
  }
  return () => {
    cancelAnimationFrame(frameId);
    viewport.removeEventListener("scroll", notify);
    resizeObserver.disconnect();
  };
}
export function useHeatmapViewport(resetKey) {
  const $ = _c(8);
  const viewportRef = useRef(null);
  const cachedMetricsRef = useRef(EMPTY_VIEWPORT_METRICS);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      viewportRef.current = node;
      cachedMetricsRef.current = EMPTY_VIEWPORT_METRICS;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const setViewportRef = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      cachedMetricsRef.current = EMPTY_VIEWPORT_METRICS;
      snapViewportToLatest(viewportRef.current);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== resetKey) {
    t2 = [resetKey];
    $[2] = resetKey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => readViewportMetrics(viewportRef.current, cachedMetricsRef);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const getViewportSnapshot = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = onStoreChange => subscribeToViewportMetrics(viewportRef.current, onStoreChange);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const subscribeToViewport = t4;
  const viewportMetrics = useSyncExternalStore(subscribeToViewport, getViewportSnapshot, _temp);
  let t5;
  if ($[6] !== viewportMetrics) {
    t5 = {
      viewportRef: setViewportRef,
      viewportMetrics
    };
    $[6] = viewportMetrics;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function _temp() {
  return EMPTY_VIEWPORT_METRICS;
}